import React, { useState, useMemo, useCallback } from "react";
import { Table, Button, Modal, Select, Tag } from "antd";
import TemplateForm from './TemplateForm';
import {
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  TableContent,
  TableScrollArea,
  StyledFiltersSection,
  FiltersGroup,
  FilterSelect,
  FilterInput,
  CapitalizedText,
  ActionsCell,
} from './Templates.styles';
import { useTelemedicineCategoryContext } from "../../../context/TelemedicineCategoryContext";

const TemplatesTable = React.memo(({ 
  loading,
  type, 
  templates, 
  templateFilters, 
  caseStatuses,
  physicians,
  setTemplateFilters,
  searchTemplates,
  handleFinish = () => {}
}) => {
  const [templateFormIsOpen, setTemplateFormIsOpen] = useState(false);
  const [templateFormMode, setTemplateFormMode] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const { telemedicineCategories } = useTelemedicineCategoryContext()
  
  const selectedTemplate = useMemo(() => {
    return templates.find(template => template._id === selectedTemplateId) || {}; 
  }, [templates, selectedTemplateId])

  const openTemplateForm = useCallback((mode, template) => {
    setTemplateFormMode(mode);
    setTemplateFormIsOpen(true);
    setSelectedTemplateId(template._id);
  }, [])

  const _handleFinish = () => {
    handleFinish()
    setTemplateFormIsOpen(false)
    searchTemplates?.({ type, ...templateFilters })
  }

  const columns = useMemo(() => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: '20%',
    },
    ...(
      type === 'case'
        ? [{
            title: "Telemedicine Category",
            dataIndex: "teleMedicineCategories",
            key: "telemedicine-category",
            width: '20%',
            render: val => <CapitalizedText>{val}</CapitalizedText>
        },
          {
            title: "Assigned Case Status",
            dataIndex: "caseStatus",
            key: "case-status",
            width: '20%',
            render: val => <CapitalizedText>{val}</CapitalizedText>
          }]
        : []
    ),
    {
      title: 'Owner',
      width: '12%',
      render: (template) => <CapitalizedText>{!!template.owner ? `${template.owner.firstName} ${template.owner.lastName}` : 'Admin'}</CapitalizedText>
    },
    {
      title: 'NDCs',
      span: '8%',
      render: (template) => <CapitalizedText>{(template.ndcs || []).map(ndc => <Tag key={ndc}>{ndc}</Tag>)}</CapitalizedText>
    },
    {
      title: 'Actions',
      align: "center",
      width: 120,
      render: (template) => (
        <ActionsCell>
          <Button
            shape="circle"
            icon={<EyeOutlined />}
            onClick={() => openTemplateForm('View', template)}
          />
          <Button
            shape="circle"
            icon={<EditOutlined />}
            onClick={() => openTemplateForm('Edit', template)}
          />
        </ActionsCell>
      ),
    }
  ], [type, openTemplateForm])

  return (
    <TableContent>
      <StyledFiltersSection>
        <FiltersGroup>
            <FilterSelect
              allowClear
              placeholder="Template Owner"
              onChange={(value) => setTemplateFilters(filters => ({ ...filters, owner: value }))} popupMatchSelectWidth={false}
            >
              <Select.Option value='admin'>Admin</Select.Option>
              {physicians?.map((physician) => <Select.Option key={physician._id} value={physician._id}>{physician.firstName} {physician.lastName}</Select.Option>)}
            </FilterSelect>
          {type === 'case' && (
            <>
                <FilterSelect
                  allowClear
                  placeholder="Telemedicine Category"
                  onChange={(value) => setTemplateFilters(filters => ({ ...filters, categoryIds: value && [value] }))}
                  popupMatchSelectWidth={false}
                >
                  {telemedicineCategories?.map(category => <Select.Option key={category._id} value={category._id}>{category.name}</Select.Option>)}
                </FilterSelect>

                <FilterSelect
                  allowClear
                   placeholder="Case Status"
                   onChange={(value) => setTemplateFilters(filters => ({ ...filters, status: value }))}
                  popupMatchSelectWidth={false}
                >
                  {caseStatuses?.map(status => <Select.Option key={status._id} value={status.name}>{status.name}</Select.Option>)}
                </FilterSelect>
            </>
          )}
        </FiltersGroup>
          <FilterInput
            value={templateFilters.search ?? ''}
            placeholder="Search"
            onChange={({ target }) => setTemplateFilters(filters => ({ ...filters, search: target.value }))}
          />
      </StyledFiltersSection>
      <TableScrollArea>
        <Table
          loading={loading}
          columns={columns}
          dataSource={templates}
          pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
          scroll={{ y: 'calc(100vh - 390px)', x: true }}
        />
      </TableScrollArea>
      <Modal
        maskClosable
        centered
        width='50vw'
        closable={false}
        footer={null}
        open={templateFormIsOpen}
        onCancel={() => setTemplateFormIsOpen(false)}
        destroyOnClose
      >
        <TemplateForm
          mode={templateFormMode}
          type={type}
          telemedicineCategories={telemedicineCategories}
          template={selectedTemplate}
          handleFinish={_handleFinish}
        />
      </Modal>
    </TableContent>
  );
})

export default TemplatesTable;